import { useEffect, useRef, useCallback } from 'react';

const RECONNECT_DELAY = 3000;

export function useWebSocket(url, { onMessage, onConnect, oaIds = [] } = {}) {
  const ws             = useRef(null);
  const reconnectTimer = useRef(null);
  const isFirstConnect = useRef(true);
  const onMessageRef   = useRef(onMessage);
  const onConnectRef   = useRef(onConnect);
  onMessageRef.current = onMessage;
  onConnectRef.current = onConnect;

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;

    ws.current = new WebSocket(url);

    ws.current.onopen = () => {
      console.log('[WS] Connected');
      // On reconnect (not first connect) notify so caller can reload missed messages
      if (!isFirstConnect.current) {
        onConnectRef.current?.();
      }
      isFirstConnect.current = false;
    };

    ws.current.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        onMessageRef.current?.(event);
      } catch (_) {}
    };

    ws.current.onclose = () => {
      console.log('[WS] Disconnected — retrying in 3s');
      reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY);
    };

    ws.current.onerror = (err) => {
      console.error('[WS] Error:', err);
      ws.current.close();
    };
  }, [url]); // eslint-disable-line

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimer.current);
      ws.current?.close();
    };
  }, [connect]);

  // Re-send subscription whenever oaIds changes (e.g. after initial API load)
  const oaIdsKey = oaIds.join(',');
  useEffect(() => {
    if (!oaIdsKey || !ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify({ type: 'subscribe', oa_ids: oaIdsKey.split(',').map(Number) }));
  }, [oaIdsKey]);
}
